import { Component, OnInit, Input, ViewChild, AfterViewInit, ChangeDetectionStrategy, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { MatPaginator, MatSort, MatTable } from '@angular/material';
import { DataTableSource } from '../models/data-table-source';
import { DataTableDefinition } from '../models/data-table-definition';
import { DataTableColumn } from '../models/data-table-column';
import { DataTableConventionService } from '../services/data-table-convention.service';
import { merge, isObservable, pipe, Subscription } from 'rxjs';
import { PagedDataRequest, FilterSet, FilterBuilder } from 'src/app/stp/stp-app/api-client';
import { DataTableRowAction } from '../models/data-table-row-action';
import * as _ from 'lodash';

@Component({
  selector: 'stp-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent<T> implements OnInit, AfterViewInit {
  private readonly NO_CELL_VALUE = '-';
  private readonly DEFAULT_PAGE_SIZE = 25;

  @Input() config: DataTableDefinition<T>;
  @Input() dataSource: DataTableSource<T>;
  @Input() filterSet: FilterSet;

  @ViewChild(MatTable) table: MatTable<T>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  pageSize = this.DEFAULT_PAGE_SIZE;
  columns: DataTableColumn<T>[];
  rowActions: DataTableRowAction<T>[];
  columnNames: string[];
  initialSortColumnName: keyof T | string;
  initialSortDirection: 'asc' | 'desc';
  cellValueSubscriptions: Subscription[] = [];

  constructor(private dataTableConventionService: DataTableConventionService<T>,
      private filterBuilder: FilterBuilder
    ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterSet && !changes.filterSet.firstChange) {
      this.render();
    }
  }

  ngOnInit() {
    this.config = this.dataTableConventionService.applyConventions(this.config);

    this.columns = this.config.columns;
    this.columnNames = this.getColumnNames(this.config);
    this.rowActions = this.config.rowActions;

    this.initialSortColumnName = this.config.initialSortBy || this.columnNames[0];
    this.initialSortDirection = this.config.initialSortDirection || 'asc';

    // Create macro-task
    setTimeout(() => this.render(), 0);
  }

  ngAfterViewInit(): void {

    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page)
      .subscribe(() => {
        this.render();
      });
  }

  ngOnDestroy() {
    _.forEach(this.cellValueSubscriptions, x => x.unsubscribe());
  }

  get hasNoData() {
    return !this.dataSource.loading && this.dataSource.dataLength === 0 && !this.dataSource.errored;
  }

  get hasError() {
    return !this.dataSource.loading && this.dataSource.errored;
  }

  get hasRowClick() {
    return !!this.config.onRowClick;
  }

  getCellValue(dataItem: any, column: DataTableColumn<T>) {

    const cellValue = {
      value: null
    };

    const value = column.valueProvider(dataItem) || this.NO_CELL_VALUE;

    if (!isObservable(value)) {
      cellValue.value = value;
    } else {
      this.cellValueSubscriptions.push(
        value.subscribe((result) => cellValue.value = result)
      );
    }

    return cellValue;
  }

  onRowClick(dataItem: T) {
    if (this.hasRowClick) {
      this.config.onRowClick(dataItem);
    }
  }

  onRowActionClick(rowAction: DataTableRowAction<T>, dataItem: T) {
    if (rowAction.onClick) {
      rowAction.onClick(dataItem);
    }
  }

  public render() {
    const pagedDataRequest = <PagedDataRequest>{
      page: this.paginator.pageIndex + 1,
      pageSize: this.paginator.pageSize
    };

    if (this.filterSet) {
      pagedDataRequest.filter = this.filterBuilder.buildUriComponent(this.filterSet);
    }

    if (this.sort.active) {
      pagedDataRequest.sort = `${this.sort.active}-${this.sort.direction}`;
    }

    this.dataSource.load(pagedDataRequest);
  }

  private getColumnNames(config: DataTableDefinition<T>) {
    const names = config.columns.map(column => column.name.toString());
    if (config.rowActions) {
      names.push('row-actions');
    }
    return names;
  }
}
